/**
 * Inline editor for a single workflow scenario.
 *
 * Renders one field per value input (respecting type/required/options) and a
 * small credential group per credential input. Default values pre-fill from
 * the input definition when creating a new scenario.
 *
 * @module FlowDesigner/components/ScenarioEditor
 */

import React from 'react';
import { Button, Flex, Select, Switch, Text, TextField } from '@chatsdet/ui';
import { Check, X } from '@phosphor-icons/react';
import type {
  WorkflowInputDefinition,
  WorkflowScenario,
  WorkflowValueInputDefinition,
  WorkflowCredentialInputDefinition,
} from '@chatsdet/types';
import { validateWorkflowInputExpressions } from '@chatsdet/types';
import type { CredentialTemplate } from '../../../../hooks/useCredentialTemplates';
import { MacroRichTextField } from '../../../components/node-config/MacroRichTextField';

interface ScenarioEditorProps {
  inputs: WorkflowInputDefinition[];
  initial: WorkflowScenario;
  isSaving?: boolean;
  onSave: (scenario: WorkflowScenario) => void;
  onCancel: () => void;
  credentialTemplates?: CredentialTemplate[];
}

function deriveInitialValues(
  inputs: WorkflowInputDefinition[],
  initial: WorkflowScenario
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const def of inputs) {
    if (def.kind !== 'value') continue;
    out[def.name] = initial.values?.[def.name] ?? def.defaultValue ?? '';
  }
  return out;
}

function deriveInitialCredentials(
  inputs: WorkflowInputDefinition[],
  initial: WorkflowScenario
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const def of inputs) {
    if (def.kind !== 'credential') continue;
    const existing = initial.credentials?.[def.name] ?? {};
    const defaults = def.defaults ?? {};
    out[def.name] = {
      credentialTemplateId: existing.credentialTemplateId ?? def.defaultCredentialTemplateId ?? '',
      ...(def.credentialFields.username
        ? { username: existing.username ?? defaults.username ?? '' }
        : {}),
      ...(def.credentialFields.password
        ? { password: existing.password ?? defaults.password ?? '' }
        : {}),
      ...(def.credentialFields.loginUrl
        ? { loginUrl: existing.loginUrl ?? defaults.loginUrl ?? '' }
        : {}),
      ...(def.credentialFields.mfaSecret ? { mfaSecret: existing.mfaSecret ?? '' } : {}),
    };
  }
  return out;
}

// Basic, forgiving email shape check — mirrors what the browser's native
// `type="email"` accepts (something@something.tld) without overreaching.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate a single scenario value against its input type. Empty values are
 * always allowed here (they fall back to the input's default at run time);
 * we only flag values that are present but malformed.
 */
function validateValueInput(def: WorkflowValueInputDefinition, value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  // Dynamic expressions are validated separately against the shared macro and
  // ordered-variable contract; their resolved value is type-checked at run time.
  if (trimmed.includes('$')) return null;
  if (def.type === 'email') {
    return EMAIL_RE.test(trimmed) ? null : 'Enter a valid email address.';
  }
  if (def.type === 'number') {
    return Number.isFinite(Number(trimmed)) ? null : 'Enter a valid number.';
  }
  return null;
}

export const ScenarioEditor: React.FC<ScenarioEditorProps> = ({
  inputs,
  initial,
  isSaving,
  onSave,
  onCancel,
  credentialTemplates,
}) => {
  const [name, setName] = React.useState(initial.name);
  const [description, setDescription] = React.useState(initial.description ?? '');
  const [isDefault, setIsDefault] = React.useState(!!initial.isDefault);
  // Treat undefined as included; only explicit false excludes (matches backend).
  const [includeInSuite, setIncludeInSuite] = React.useState(initial.includeInSuite !== false);
  const [values, setValues] = React.useState<Record<string, string>>(() =>
    deriveInitialValues(inputs, initial)
  );
  const [credentials, setCredentials] = React.useState<Record<string, Record<string, string>>>(() =>
    deriveInitialCredentials(inputs, initial)
  );

  const valueInputs = inputs.filter((d): d is WorkflowValueInputDefinition => d.kind === 'value');
  const credentialInputs = inputs.filter(
    (d): d is WorkflowCredentialInputDefinition => d.kind === 'credential'
  );

  const isNameValid = name.trim().length > 0;
  const expressionIssues = React.useMemo(
    () => validateWorkflowInputExpressions(inputs, values),
    [inputs, values]
  );

  const valueErrors = React.useMemo(() => {
    const errs: Record<string, string> = {};
    for (const issue of expressionIssues) {
      errs[issue.inputName] ??= issue.message.replaceAll('`', '');
    }
    for (const def of valueInputs) {
      const error = validateValueInput(def, values[def.name] ?? '');
      if (error) errs[def.name] ??= error;
    }
    return errs;
  }, [expressionIssues, valueInputs, values]);
  const hasValueErrors = Object.keys(valueErrors).length > 0;

  const handleSave = () => {
    if (!isNameValid || hasValueErrors) return;
    // Drop empty strings so the stored scenario doesn't persist placeholders.
    const cleanValues: Record<string, string> = {};
    for (const [k, v] of Object.entries(values)) if (v !== '') cleanValues[k] = v;

    const cleanCredentials: Record<string, Record<string, string>> = {};
    for (const [credName, fields] of Object.entries(credentials)) {
      const nonEmpty: Record<string, string> = {};
      for (const [k, v] of Object.entries(fields)) if (v !== '') nonEmpty[k] = v;
      if (Object.keys(nonEmpty).length > 0) cleanCredentials[credName] = nonEmpty;
    }

    onSave({
      id: initial.id,
      name: name.trim(),
      description: description.trim() || undefined,
      isDefault: isDefault ? true : undefined,
      // Only persist explicit exclusion; undefined = included (matches backend semantics).
      includeInSuite: includeInSuite ? undefined : false,
      values: Object.keys(cleanValues).length > 0 ? cleanValues : undefined,
      credentials: Object.keys(cleanCredentials).length > 0 ? cleanCredentials : undefined,
    });
  };

  const renderValueField = (def: WorkflowValueInputDefinition) => {
    const label = def.required ? `${def.label} *` : def.label;
    const value = values[def.name] ?? '';
    const error = valueErrors[def.name];
    const update = (v: string) => setValues((prev) => ({ ...prev, [def.name]: v }));
    const definitionIndex = inputs.indexOf(def);
    const suggestionVariables = inputs
      .slice(0, definitionIndex)
      .filter(
        (previous): previous is WorkflowValueInputDefinition =>
          previous.kind === 'value' && previous.name.trim().length > 0
      )
      .map((previous) => ({
        kind: 'variable' as const,
        name: previous.name,
        source: 'workflowInput' as const,
        description: 'Earlier workflow input',
        designTimeValue: values[previous.name] ?? previous.defaultValue,
      }));

    if (def.type === 'textarea') {
      return (
        <MacroRichTextField
          key={def.name}
          label={label}
          aria-label={label}
          description={def.description}
          placeholder={def.placeholder}
          value={value}
          multiline
          rows={3}
          includeVariables
          suggestionVariables={suggestionVariables}
          errorMessage={error}
          onChange={update}
        />
      );
    }

    if (def.type === 'select' && def.options) {
      const items = def.options.map((opt) => ({ id: opt.value, label: opt.label }));
      return (
        <Select
          key={def.name}
          label={label}
          items={items}
          placeholder="Select..."
          selectedKey={value || null}
          onSelectionChange={(key) => update(key as string)}
        />
      );
    }

    if (def.type !== 'password') {
      return (
        <MacroRichTextField
          key={def.name}
          label={label}
          aria-label={label}
          description={def.description}
          placeholder={def.placeholder ?? 'Type $ for macros or earlier inputs'}
          errorMessage={error}
          value={value}
          includeVariables
          suggestionVariables={suggestionVariables}
          onChange={update}
        />
      );
    }

    return (
      <TextField
        key={def.name}
        label={label}
        description={def.description}
        placeholder={def.placeholder}
        type="password"
        errorMessage={error}
        value={value}
        onChange={update}
      />
    );
  };

  const renderCredentialGroup = (def: WorkflowCredentialInputDefinition) => {
    const current = credentials[def.name] ?? {};
    const updateField = (field: string, v: string) =>
      setCredentials((prev) => ({ ...prev, [def.name]: { ...prev[def.name], [field]: v } }));
    const templateItems = (credentialTemplates ?? []).map((template) => ({
      id: template.id,
      label: template.name,
    }));

    return (
      <div key={def.name} className="rounded-md border border-border p-2 space-y-2">
        <Text size="xs" className="font-mono text-muted-foreground">
          Credential: $var.{def.name}
        </Text>
        {templateItems.length > 0 ? (
          <Select
            label="Persona"
            items={templateItems}
            placeholder="Select persona..."
            selectedKey={current.credentialTemplateId || null}
            onSelectionChange={(key) => updateField('credentialTemplateId', key as string)}
          />
        ) : (
          <TextField
            label="Persona ID"
            value={current.credentialTemplateId ?? ''}
            onChange={(v) => updateField('credentialTemplateId', v)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3 rounded-md border border-border bg-muted/30 p-3">
      <TextField
        label="Name *"
        placeholder="e.g. Happy path"
        value={name}
        onChange={setName}
        errorMessage={!isNameValid ? 'Required' : undefined}
      />
      <TextField
        label="Description"
        placeholder="Optional"
        value={description}
        onChange={setDescription}
      />
      <Flex gap="md" wrap>
        <Switch isSelected={isDefault} onChange={setIsDefault} label="Use as default" />
        <Switch
          isSelected={includeInSuite}
          onChange={setIncludeInSuite}
          label="Include in test suite"
        />
      </Flex>

      {valueInputs.length > 0 && (
        <div className="space-y-2">
          <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
            Inputs
          </Text>
          {valueInputs.map(renderValueField)}
        </div>
      )}

      {credentialInputs.length > 0 && (
        <div className="space-y-2">
          <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
            Credentials
          </Text>
          {credentialInputs.map(renderCredentialGroup)}
        </div>
      )}

      <Flex gap="sm" justify="end">
        <Button
          variant="outline"
          size="sm"
          onPress={onCancel}
          isDisabled={isSaving}
          iconLeft={<X size={14} />}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onPress={handleSave}
          isDisabled={!isNameValid || hasValueErrors || isSaving}
          iconLeft={<Check size={14} />}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Flex>
    </div>
  );
};
